import {makeAutoObservable, observable, action} from 'mobx';
import {ViewController} from 'data/types/structure';
import {injectable, inject} from 'inversify';
import {Bindings} from 'data/constants/bindings';
import type {ILocalizationStore} from 'data/stores/localization/localization.store';
import type {SyntheticEvent, ChangeEvent} from 'react';
import {useNavigate} from 'react-router-dom';
import {ModalType} from 'data/enums';
import {every} from 'lodash';
import type {IUserStore} from 'data/stores/user/user.store';
import type {IModalsStore} from 'data/stores/modals/modals.store';
import type {IGigyaGateStore} from 'data/stores/gigya_gate/gigya_gate.store';
import {createConnextraScript, ConnextraType} from 'data/utils/createConnextraScript';
import type {AxiosError} from 'axios';
import type {IApiResponse} from 'data/services/http';
import {extractErrorMessage} from 'data/utils';


interface IProps {
    navigate: ReturnType<typeof useNavigate>;
}

interface IRegistrationFormElement extends HTMLFormElement {
    displayName: HTMLInputElement;
    terms: HTMLInputElement;
    isNotificationsEnabled: HTMLInputElement;
    isOptIn: HTMLInputElement;
}


interface IRegistrationFormData {
    terms: boolean;
}

export interface IRegistrationTermsController extends ViewController<IProps> {
    readonly i18n: ILocalizationStore;

    get isFormDisabledByData(): boolean;
    get hasAcceptedTerms(): boolean;

    get error(): Record<string, string> | null;

    handleFormSubmit: (event: SyntheticEvent<IRegistrationFormElement>) => void;
    handleFormOnChange: (event: ChangeEvent<IRegistrationFormElement>) => void;
    handleCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

@injectable()
export class RegistrationTermsController implements IRegistrationTermsController {
    @observable private _navigate?: ReturnType<typeof useNavigate>;
    @observable private _errorMsg: string | null = null;
    @observable private _errorPlace = "";
    @observable private _formData: IRegistrationFormData = {
        terms: false,
    };

    get error() {
        if( !this._errorMsg ) return null;

        return {
            [ this._errorPlace || "common" ]: this._errorMsg,
        };
    }


    get isFormDisabledByData(): boolean {
        return every([!this._formData.terms]);
    }

    get hasAcceptedTerms() {
        return !!this._userStore.user?.isTermsAccepted
    }
    constructor(
        @inject(Bindings.UserStore) private _userStore: IUserStore,
        @inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
        @inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
        @inject(Bindings.GigyaGateStore) readonly _gigyaGateStore: IGigyaGateStore
    ) {
        makeAutoObservable(this);
    }

    init({navigate}: IProps) {
        this._navigate = navigate;

        createConnextraScript(ConnextraType.REGISTRATION_START, this._userStore.user?.id);
    }

    @action
    private reportError(error: string, place: string = "") {
        this._errorMsg = error;
        this._errorPlace = place;

        return true;
    }

    @action public handleFormSubmit = (event: SyntheticEvent<IRegistrationFormElement>) => {
        event.preventDefault();

        const {terms} = event.currentTarget;


        const errorInvalidTerms = this.i18n.t(
            "registration_terms.terms.error",
            "Please accept Terms & Conditions"
        );

        const validateList = [
            {field: terms, error: errorInvalidTerms, place: "terms"},
        ];

        const hasError = validateList.find(({field, error, place}) =>
            field.checkValidity() ? false : this.reportError(error, place)
        );

        if( hasError ) {
            return;
        }


        this._userStore
            .acceptTerms()
            .then(() => {
                this._navigate?.("/");
            })
            .catch(this.onError);
    };

    @action private onError = (error: AxiosError<IApiResponse>) => {
        this._errorMsg = extractErrorMessage(error);

        this._modalsStore.showModal(ModalType.ERROR, {
            message: this._errorMsg,
        });
    };

    @action handleFormOnChange = (event: ChangeEvent<IRegistrationFormElement>) => {
        if( event.target.name !== "password" ) {
            this._errorMsg = null;
            this._errorPlace = "";
        }
    };

    @action private setFormData = (name: string, checked: boolean) => {
        this._formData[ name as keyof IRegistrationFormData ] = checked;
    };

    @action handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        const {name, checked} = event.target;

        this.setFormData(name, checked);
    };
}